import { FC, useState } from 'react';
import { css } from '@emotion/react';
import { SetActiveDeviceResponse } from '@weave/schema-gen-ts/dist/schemas/phone/user/v1/user_service.pb';
import { useQuery } from 'react-query';
import { Device, useQueryDevices } from '@frontend/api-phone-sync';
import { UsersApi } from '@frontend/api-users';
import { getUser } from '@frontend/auth-helpers';
import { Trans, useTranslation } from '@frontend/i18n';
import { getFullName } from '@frontend/name';
import { usePhoneConfigShallowStore } from '@frontend/phone-config';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { SoftphoneStateInterface } from '@frontend/softphone-state';
import { theme } from '@frontend/theme';
import {
  Button,
  ButtonBar,
  CaretLeftIconSmall,
  Modal,
  ModalControlResponse,
  SearchField,
  Text,
  TextLink,
  useFormField,
} from '@frontend/design-system';
import { usePhoneSyncMutations } from '../use-phone-sync-mutations';
import { isLocationGroup } from '../utils';
import { DeviceSection } from './device-section';

const VARIANTS = {
  DIALPAD: 'dialpad',
  LOCATION_PICKER: 'location-picker',
  CLICK_TO_CALL: 'click-to-call',
} as const;
interface ModalProps {
  closeModal?: () => void;
  deviceSelectModal: ModalControlResponse;
  locationIds: string[];
  pairDeviceButton?: React.ReactNode;
  variant: (typeof VARIANTS)[keyof typeof VARIANTS];
  onConnectSuccess?: (data: SetActiveDeviceResponse) => void;
}

export const DeviceSelectModal: FC<React.PropsWithChildren<ModalProps>> = ({
  closeModal,
  deviceSelectModal,
  locationIds,
  variant,
  pairDeviceButton,
  onConnectSuccess,
}) => {
  const { phoneConfig } = usePhoneConfigShallowStore('phoneConfig');
  const { connectDeviceAsync, disconnectDevice } = usePhoneSyncMutations();
  const [screen, setScreen] = useState<
    | { type: 'deviceSelection' }
    | { type: 'overwritingAccess'; payload: { device: Device } }
    | { type: 'continueWithoutDevice' }
  >({ type: 'deviceSelection' });
  const { activateCurrentTab } = SoftphoneStateInterface;

  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const hasDeviceConnectionChangeFlag = getFeatureFlagValue('device-connection-change');

  const connectDevice = (data: Device) => {
    connectDeviceAsync({ sipProfileId: data.sipProfileId ?? '' }).then((res) => {
      activateCurrentTab();
      onConnectSuccess?.(res);
    });
    deviceSelectModal?.closeModal();
    closeModal?.();
  };

  const initiateConnectDevice = (data: Device) => {
    const user = getUser();

    if (hasDeviceConnectionChangeFlag && !!data.activeUserId && user?.userID !== data.activeUserId) {
      setScreen({ type: 'overwritingAccess', payload: { device: data } });
      return;
    }
    connectDevice(data);
  };

  const initiateContinueWithoutDevice = () => {
    if (phoneConfig) {
      setScreen({ type: 'continueWithoutDevice' });
    } else {
      disconnectDevice();
      closeModal?.();
    }
  };

  const resetScreen = () => setScreen({ type: 'deviceSelection' });

  return (
    <>
      <Modal
        {...deviceSelectModal.modalProps}
        maxWidth={600}
        onClose={() => {
          resetScreen();
          deviceSelectModal.closeModal();
          closeModal?.();
        }}
      >
        {screen.type === 'deviceSelection' && (
          <DeviceSelectionScreen
            deviceSelectModal={deviceSelectModal}
            locationIds={locationIds}
            variant={variant}
            pairDeviceButton={pairDeviceButton}
            onConnectSuccess={onConnectSuccess}
            connectDeviceAsync={connectDeviceAsync}
            phoneConfig={phoneConfig}
            initiateConnectDevice={initiateConnectDevice}
            initiateContinueWithoutDevice={initiateContinueWithoutDevice}
          />
        )}
        {hasDeviceConnectionChangeFlag && screen.type === 'overwritingAccess' && (
          <ConfirmDeviceOverwriteAccessScreen
            resetScreen={resetScreen}
            connectDevice={connectDevice}
            {...screen.payload}
          />
        )}
        {screen.type === 'continueWithoutDevice' && (
          <ConfirmContinueWithoutDeviceScreen
            onCancel={() => {
              resetScreen();
            }}
            onConfirm={() => {
              if (phoneConfig) {
                disconnectDevice();
              }
              closeModal?.();
              resetScreen();
            }}
          />
        )}
      </Modal>
    </>
  );
};

const DeviceSelectionScreen = ({
  deviceSelectModal,
  locationIds,
  variant,
  pairDeviceButton,
  phoneConfig,
  initiateConnectDevice,
  initiateContinueWithoutDevice,
}: ModalProps & {
  connectDeviceAsync: (data: { sipProfileId: string }) => Promise<SetActiveDeviceResponse>;
  phoneConfig?: Device;
  initiateConnectDevice: (data: Device) => void;
  initiateContinueWithoutDevice: () => void;
}) => {
  const { t } = useTranslation('phoneSync');
  const fieldProps = useFormField({ type: 'text' });
  const { accessibleLocationData } = useAppScopeStore();

  const { data: devices } = useQueryDevices(locationIds);
  const user = getUser();

  const filteredAvailableDevices = devices?.filter((device) => {
    return (
      device.sipProfileId !== phoneConfig?.sipProfileId &&
      device.availableInContext &&
      device.name?.toLowerCase().includes(fieldProps.value.toLowerCase())
    );
  });

  let actionComponent;

  if (variant === VARIANTS.LOCATION_PICKER) {
    actionComponent = (
      <Modal.Footer
        tertiary={{
          label: (
            <span style={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
              <CaretLeftIconSmall /> {t('Go Back')}
            </span>
          ),
          onClick: () => {
            deviceSelectModal.closeModal();
          },
          trackingId: 'phn-portal-selectDevice-btn-goBack',
        }}
        secondary={{
          label: t('Continue Without Device'),
          onClick: () => {
            initiateContinueWithoutDevice();
          },
          trackingId: 'phn-portal-selectDevice-btn-continueWithoutDevice',
        }}
      />
    );
  } else if (variant === VARIANTS.DIALPAD) {
    actionComponent = (
      <Modal.Footer
        secondary={{
          label: t('Cancel'),
          onClick: () => {
            deviceSelectModal.closeModal();
          },
          trackingId: 'phn-portal-selectDevice-btn-continueWithoutDevice',
        }}
      />
    );
  } else if (variant === VARIANTS.CLICK_TO_CALL) {
    actionComponent = (
      <Modal.Footer
        css={css`
          grid-template-columns: 1fr auto;
          justify-items: end;
        `}
      >
        <TextLink
          weight='bold'
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(0.5),
          }}
          onClick={() => {
            deviceSelectModal.closeModal();
          }}
          trackingId='phn-portal-selectDevice-lnk-goBack'
        >
          {t('Cancel')}
        </TextLink>
        {pairDeviceButton}
      </Modal.Footer>
    );
  }

  return (
    <>
      <Modal.Header textAlign='left'>{t('Select Device')}</Modal.Header>
      <Modal.Body>
        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
          <Text css={css`margin: 0;}`}>
            {t(
              'The location you selected belongs to different phone systems. Since you can be connected to only one device within one phone system at a time, please select the device you would like to use to make or receive calls.',
              { count: locationIds.length }
            )}
          </Text>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <SearchField
              name='search'
              css={css`
                width: ${theme.spacing(40)};
              `}
              {...fieldProps}
            />
            {variant === VARIANTS.LOCATION_PICKER && pairDeviceButton}
          </div>
          <div css={{ paddingBottom: theme.spacing(2) }}>
            {filteredAvailableDevices?.map((item, index) => (
              <DeviceSection
                key={item.sipProfileId}
                item={item}
                currentUserId={user?.userID ?? ''}
                index={index}
                deviceSelectModal={deviceSelectModal}
                handleConnectDeviceModal={initiateConnectDevice}
                isLocationGroup={isLocationGroup(item, accessibleLocationData)}
              />
            ))}
          </div>
        </div>
      </Modal.Body>
      {actionComponent}
    </>
  );
};

const ConfirmContinueWithoutDeviceScreen = ({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  const { t } = useTranslation('phoneSync');

  return (
    <>
      <Modal.Header>{t('Continue Without Device?')}</Modal.Header>
      <Modal.Body>
        <Text>
          {t(
            'Continuing without connecting a device will prevent you from placing or receiving calls within the Weave app.'
          )}
        </Text>
      </Modal.Body>
      <Modal.Footer
        secondary={{
          trackingId: 'phn-portal-selectDevice-btn-NoGoBack',
          label: t('No, Go Back'),
          onClick: () => {
            onCancel();
          },
        }}
        primary={{
          trackingId: 'phn-portal-selectDevice-btn-yesContinue',
          label: t('Yes, Continue'),
          onClick: () => {
            onConfirm();
          },
        }}
      />
    </>
  );
};

const ConfirmDeviceOverwriteAccessScreen = ({
  resetScreen,
  device,
  connectDevice,
}: {
  resetScreen: () => void;
  device: Device;
  connectDevice: (data: Device) => void;
}) => {
  const { t } = useTranslation('phoneSync');

  const { data } = useQuery({
    queryKey: ['user', device.activeUserId],
    queryFn: () => {
      return UsersApi.list.getUserProfileByUserID(device.activeUserId ?? '');
    },
    enabled: !!device.activeUserId,
  });

  return (
    <>
      <Modal.Header>{t('Overwrite Device Access')}</Modal.Header>
      <Modal.Body>
        {data && (
          <Trans t={t}>
            <Text>
              You are about to take over this device from{' '}
              <Text as='span' weight='bold'>
                {getFullName(data)}
              </Text>
              .
            </Text>
            <Text>
              This will disconnect the user from any call notifications directed at the device. Do you want to proceed?
            </Text>
          </Trans>
        )}
      </Modal.Body>
      <Modal.Footer
        secondary={{
          label: t('Cancel'),
          onClick: () => {
            resetScreen();
          },
        }}
        primary={{
          label: t('Proceed'),
          onClick: () => {
            connectDevice(device);
            resetScreen();
          },
        }}
      />
    </>
  );
};

export const LosingAccessModal = ({
  modal,
  pairDeviceButton,
  onContinue,
}: {
  modal: ModalControlResponse;
  pairDeviceButton?: React.ReactNode;
  onContinue: () => void;
}) => {
  const { t } = useTranslation('phoneSync');

  return (
    <Modal {...modal.modalProps} maxWidth={600}>
      <Modal.Header textAlign='left'>{t('Losing Device Access')}</Modal.Header>
      <Modal.Body>
        <Trans t={t}>
          <Text css={{ marginBlock: '1em' }}>
            Based on your location selection, you will no longer have access to a device. As a result, you will not be
            able to place or receive calls within the Weave app.
          </Text>
          <Text css={{ marginBlock: '1em' }}>
            To resume device access, either update your location selection or pair a new device here.
          </Text>
        </Trans>
      </Modal.Body>
      <Modal.Footer>
        <ButtonBar
          removeSpacing
          css={css`
            display: flex;
            gap: ${theme.spacing(2)};
            justify-content: space-between;
          `}
        >
          <TextLink
            weight='bold'
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing(0.5),
              width: 'auto',
            }}
            onClick={() => {
              modal.closeModal();
            }}
            trackingId='phn-portal-losingAccess-btn-continue'
            data-test-id='loseAccess-goBack-btn'
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
              <CaretLeftIconSmall /> {t('Go Back')}
            </span>
          </TextLink>

          <div style={{ display: 'flex', gap: theme.spacing(1) }}>
            {pairDeviceButton}

            <Button
              css={{ width: 'auto' }}
              size='large'
              onClick={() => {
                modal.closeModal();
                onContinue();
              }}
              trackingId='phn-portal-losingAccess-btn-continue'
            >
              {t('Continue')}
            </Button>
          </div>
        </ButtonBar>
      </Modal.Footer>
    </Modal>
  );
};
